/**
 * "Group Chat Picks" section – videos shared in the Thursday Jam group chat.
 * Uses a static pre-resolved config – no runtime API calls.
 */
import { useMemo } from "react";
import chatVideos from "@/data/chatVideos";
import VariableHeadline from "@/components/VariableHeadline";

interface ChatVideoSectionProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const ChatVideoSection = ({ selectedId, onSelect }: ChatVideoSectionProps) => {
  const sortedVideos = useMemo(
    () => [...chatVideos].sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
    []
  );

  return (
    <div className="space-y-8">
      <div>
        <div className="mb-2 h-px w-16 bg-foreground/20" />
        <VariableHeadline as="h2" pattern="crescendo" className="text-3xl md:text-4xl lg:text-5xl mb-2">
          Group Chat Picks
        </VariableHeadline>
        <p className="text-sm text-muted-foreground">
          Shared by the band in our group chat — {sortedVideos.length} videos and counting
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {sortedVideos.map((video) => (
          <button
            key={video.id}
            onClick={() => onSelect(video.id)}
            className={`
              group relative overflow-hidden rounded-sm text-left
              transition-all duration-300
              ${selectedId === video.id
                ? "ring-2 ring-foreground ring-offset-2 ring-offset-background scale-[1.02]"
                : "hover:scale-[1.03]"
              }
            `}
          >
            <div className="relative aspect-video overflow-hidden bg-muted rounded-sm">
              <img
                src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                alt={video.title || "Video thumbnail"}
                loading="lazy"
                width={320}
                height={180}
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-background/40 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-10 h-10 rounded-full bg-foreground/90 flex items-center justify-center">
                  <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-background border-b-[6px] border-b-transparent ml-1" />
                </div>
              </div>
              {selectedId === video.id && (
                <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-foreground animate-pulse" />
              )}
            </div>

            <div className="mt-2">
              <p className="text-sm font-medium text-foreground truncate">
                {video.title}
              </p>
              {video.channel && (
                <p className="text-xs text-muted-foreground truncate">
                  {video.channel}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChatVideoSection;
